/**
 * MockDetector Widget Diagnostics
 * Provides helpful debugging information for common issues
 */

export interface DiagnosticInfo {
  issue: string;
  currentDomain: string;
  possibleCauses: string[];
  solutions: string[];
  supportEmail: string;
}

/**
 * Diagnose API key authorization issues
 */
export function diagnoseApiKeyIssue(error: string): DiagnosticInfo | null {
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  
  if (error.includes('Domain not authorized') || error.includes('domain') || error.includes('authorized')) {
    return {
      issue: 'Domain Authorization Failed',
      currentDomain,
      possibleCauses: [
        'API key is not authorized for this domain',
        'Domain configuration missing in admin panel',
        'Wildcard (*) was used but specific domain is required'
      ],
      solutions: [
        `Add "${currentDomain}" to allowed domains in admin panel`,
        'Or use wildcard (*) for development/testing only',
        'Check API key configuration at admin panel',
        'Verify domain spelling matches exactly'
      ],
      supportEmail: 'fredrikssonanton01@gmail.com'
    };
  }
  
  if (error.includes('expired') || error.includes('Expired')) {
    return {
      issue: 'API Key Expired',
      currentDomain,
      possibleCauses: [
        'API key has passed its expiration date',
        'API key has expired'
      ],
      solutions: [
        'Contact support for key renewal',
        'Request new API key from admin panel'
      ],
      supportEmail: 'fredrikssonanton01@gmail.com'
    };
  }
  
  if (error.includes('limit') || error.includes('Limit')) {
    return {
      issue: 'Scan Limit Reached',
      currentDomain,
      possibleCauses: [
        'Daily scan limit exceeded',
        'Too many scans performed today'
      ],
      solutions: [
        'Wait until tomorrow for limit reset',
        'Contact support for limit increase',
        'Upgrade to higher tier if available'
      ],
      supportEmail: 'fredrikssonanton01@gmail.com'
    };
  }
  
  return null;
}

/**
 * Log diagnostic information to console
 */
export function logDiagnostics(error: string): void {
  const diagnostic = diagnoseApiKeyIssue(error);
  
  if (!diagnostic) {
    console.error('[MockDetector] Unknown error:', error);
    return;
  }
  
  console.group('[MockDetector] 🔍 Diagnostic Report');
  console.error('Issue:', diagnostic.issue);
  console.log('Current Domain:', diagnostic.currentDomain);
  console.log('Possible Causes:');
  diagnostic.possibleCauses.forEach((cause, i) => console.log(`  ${i + 1}. ${cause}`));
  console.log('Solutions:');
  diagnostic.solutions.forEach((solution, i) => console.log(`  ${i + 1}. ${solution}`));
  console.log('Support:', diagnostic.supportEmail);
  console.groupEnd();
}
